import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { Entitlements } from '@trello/entitlements';
import { intl } from '@trello/i18n';
import { Button } from '@trello/nachos/button';
import { showFlag } from '@trello/nachos/experimental-flags';
import { Popover, usePopover } from '@trello/nachos/popover';
import { Spinner } from '@trello/nachos/spinner';

import { useAddGuestMemberDetailsLazyQuery } from './AddGuestMemberDetailsQuery.generated';
import { AddGuestToWorkspacePopoverContent } from './AddGuestToWorkspacePopoverContent';
import { useAddGuestWorkspaceDetailsFragment } from './AddGuestWorkspaceDetailsFragment.generated';
import { useAddGuestToWorkspace } from './useAddGuestToWorkspace';

import styles from './AddToWorkspaceButton.less';

interface AddToWorkspaceButtonProps {
  workspaceId: string;
  memberId: string;
}

export const AddToWorkspaceButton: FunctionComponent<
  AddToWorkspaceButtonProps
> = ({ workspaceId, memberId }) => {
  const { triggerRef, toggle, popoverProps, hide } = usePopover();
  const { addGuestToWorkspace, isRequestPending } = useAddGuestToWorkspace({
    memberId,
    workspaceId,
  });

  // Since this button shows for every member when you load the workspace guests page, we don’t want to immediately run all these queries.
  // Instead, we want to be performance conscious and use the lazy query to only request the data when a user clicks on the button.
  // The data is then used to determine if the member is at/above their workspace limit, and if not, then we proceed
  const [loadGuestMemberData] = useAddGuestMemberDetailsLazyQuery({
    variables: { id: memberId },
  });

  const { data: workspaceData } = useAddGuestWorkspaceDetailsFragment({
    from: { id: workspaceId },
  });

  const isFreeWorkspace = Entitlements.isFree(workspaceData?.offering);

  const handleClick = useCallback(async () => {
    const { data } = await loadGuestMemberData();
    const memberData = data?.member;

    Analytics.sendClickedButtonEvent({
      buttonName: 'addGuestToWorkspaceButton',
      source: 'workspaceGuestsScreen',
      containers: formatContainers({ workspaceId }),
    });

    if (
      workspaceData?.limits?.orgs?.totalMembersPerOrg?.status ===
        'maxExceeded' ||
      workspaceData?.limits?.orgs?.totalMembersPerOrg?.status === 'disabled'
    ) {
      showFlag({
        id: 'workspaceAtLimit',
        title: intl.formatMessage({
          id: 'confirm.members exceeds total per org limit.title',
          defaultMessage: 'Too many members',
          description: 'Workspace at limit title',
        }),
        description: intl.formatMessage(
          {
            id: 'confirm.members exceeds total per org limit.text',
            defaultMessage:
              'There are too many members on “{orgName}”. Remove some members to add more.',
            description: 'Workspace has too many members description',
          },
          { orgName: workspaceData?.displayName },
        ),
        appearance: 'error',
      });
    } else if (
      memberData?.limits?.orgs?.totalPerMember?.status === 'maxExceeded' ||
      memberData?.limits?.orgs?.totalPerMember?.status === 'disabled'
    ) {
      showFlag({
        id: 'workspaceAtLimit',
        title: intl.formatMessage({
          id: 'confirm.orgs exceeds total per member limit.title',
          defaultMessage: 'Too many Workspaces',
          description: 'User is on too many workspaces title',
        }),
        description: intl.formatMessage(
          {
            id: 'confirm.orgs exceeds total per member limit.text',
            defaultMessage:
              '{memberName} belongs to too many Workspaces, and must be removed from some Workspaces to be added to this one.',
            description: 'Workspace has too many members description',
          },
          { memberName: memberData?.fullName },
        ),
        appearance: 'error',
      });
    } else if (isFreeWorkspace) {
      addGuestToWorkspace();
    } else {
      toggle();
    }
  }, [
    loadGuestMemberData,
    workspaceId,
    workspaceData?.limits?.orgs?.totalMembersPerOrg?.status,
    workspaceData?.displayName,
    isFreeWorkspace,
    addGuestToWorkspace,
    toggle,
  ]);

  return (
    <>
      <Button
        className={styles.addToWorkspaceButton}
        ref={triggerRef}
        onClick={handleClick}
        isDisabled={isRequestPending}
      >
        {isRequestPending ? (
          <Spinner inline small />
        ) : (
          <FormattedMessage
            id={'templates.popover_add_to_team.add to team-cta'}
            defaultMessage="Add to Workspace"
            description="Button text to add guests to a Workspace."
          />
        )}
      </Button>
      <Popover
        {...popoverProps}
        title={intl.formatMessage({
          id: 'templates.popover_add_to_team.add to team-cta',
          defaultMessage: 'Add to Workspace',
          description: 'Title for the popover to add guests to a Workspace.',
        })}
      >
        <AddGuestToWorkspacePopoverContent
          workspaceId={workspaceId}
          memberId={memberId}
          hide={hide}
        />
      </Popover>
    </>
  );
};
